import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { UpdateOrderMessageType } from "@redotech/redo-model/conversation/message-parsers/update-order-message-parser";
import { LineItem } from "@redotech/redo-model/shopify-order";
import { sinkPromise } from "@redotech/util/promise";
import classNames from "classnames";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoSpinner } from "../../arbiter-components/loading/redo-spinner";
import { Divider } from "../../divider";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { GetOrderDetailsFn, HandleUpdateOrderFn } from "../common";
import * as cancelOrderToolMessageCss from "./cancel-order-tool-message.module.css";
import { ChatContentRenderer } from "./chat-content-renderer";
import {
  GenericConfirmedState,
  GenericRejectedState,
} from "./chat-content-states";
import { ProductTileForOrder } from "./product-tile-for-order";
import * as css from "./subscribe-to-back-in-stock-tool-message.module.css";
import { getConfirmedStateContent } from "./tool-message-utils";

interface UpdateOrderToolMessageProps {
  message: UpdateOrderMessageType;
  onUpdateOrder?: HandleUpdateOrderFn;
  readOnly?: boolean;
  getOrderDetails?: GetOrderDetailsFn;
  conversationId: string;
  storeUrl?: string;
  storefrontAccessToken?: string;
}

interface OrderItemUpdate {
  variantId: string;
  quantity: number;
  action: "add" | "remove";
}

interface ItemRemovalRowProps {
  item: OrderItemUpdate;
  orderLineItems: any[];
  readOnly: boolean;
  storeUrl?: string;
  storefrontAccessToken?: string;
  onQuantityChange: (variantId: string, quantity: number) => void;
}

const ItemRemovalRow = memo(function ItemRemovalRow({
  item,
  orderLineItems,
  readOnly,
  storeUrl,
  storefrontAccessToken,
  onQuantityChange,
}: ItemRemovalRowProps) {
  const lineItem = orderLineItems.find(
    (li) =>
      li.variant_id?.toString() === item.variantId ||
      li.product_variant_id?.toString() === item.variantId,
  );

  const currentQuantity = lineItem?.current_quantity || lineItem?.quantity || 0;

  const handleQuantityChange = useCallback(
    (quantity: string) => {
      onQuantityChange(item.variantId, parseInt(quantity, 10));
    },
    [item.variantId, onQuantityChange],
  );

  const handleVariantChange = useCallback(() => {
    // No variant change for removal
  }, []);

  return (
    <ProductTileForOrder
      allowChangeVariant={false}
      maxQuantity={currentQuantity}
      onQuantityChange={handleQuantityChange}
      onVariantChange={handleVariantChange}
      quantity={item.quantity.toString()}
      quantityLabel="Amount to remove"
      readOnly={readOnly}
      selectedVariantId={item.variantId}
      showRemovalCalculation
      storefrontAccessToken={storefrontAccessToken}
      storeUrl={storeUrl}
    />
  );
});

interface ItemAdditionRowProps {
  item: OrderItemUpdate;
  readOnly: boolean;
  storeUrl?: string;
  storefrontAccessToken?: string;
  onQuantityChange: (variantId: string, quantity: number) => void;
  onVariantChange: (oldVariantId: string, newVariantId: string) => void;
}

const ItemAdditionRow = memo(function ItemAdditionRow({
  item,
  readOnly,
  storeUrl,
  storefrontAccessToken,
  onQuantityChange,
  onVariantChange,
}: ItemAdditionRowProps) {
  const handleQuantityChange = useCallback(
    (quantity: string) => {
      onQuantityChange(item.variantId, parseInt(quantity, 10));
    },
    [item.variantId, onQuantityChange],
  );

  const handleVariantChange = useCallback(
    (variantId: string) => {
      onVariantChange(item.variantId, variantId);
    },
    [item.variantId, onVariantChange],
  );

  return (
    <ProductTileForOrder
      onQuantityChange={handleQuantityChange}
      onVariantChange={handleVariantChange}
      quantity={item.quantity.toString()}
      quantityLabel="Quantity to add"
      readOnly={readOnly}
      selectedVariantId={item.variantId}
      storefrontAccessToken={storefrontAccessToken}
      storeUrl={storeUrl}
    />
  );
});

const UpdateOrderPendingState = memo(function UpdateOrderPendingState({
  message,
  onUpdateOrder,
  readOnly = false,
  getOrderDetails,
  conversationId,
  storeUrl,
  storefrontAccessToken,
}: UpdateOrderToolMessageProps) {
  const { structuredContent } = message;
  const orderLoad = useLoad(async () => {
    if (!getOrderDetails || !structuredContent.orderId) {
      return null;
    }
    return await getOrderDetails({
      orderId: structuredContent.orderId,
      conversationId,
    });
  }, [getOrderDetails, structuredContent.orderId, conversationId]);

  const [cancelButtonPending, setCancelButtonPending] = useState(false);
  const [confirmButtonPending, setConfirmButtonPending] = useState(false);
  const [items, setItems] = useState<OrderItemUpdate[]>(
    structuredContent.items,
  );

  const handleQuantityChange = useCallback(
    (variantId: string, quantity: number) => {
      setItems((prev) =>
        prev.map((item) =>
          item.variantId === variantId ? { ...item, quantity } : item,
        ),
      );
    },
    [],
  );

  const handleVariantChange = useCallback(
    (oldVariantId: string, newVariantId: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.variantId === oldVariantId
            ? { ...item, variantId: newVariantId }
            : item,
        ),
      );
    },
    [],
  );

  const confirmUpdate = useHandler(async (confirmUpdate: boolean) => {
    if (confirmUpdate) {
      setConfirmButtonPending(true);
    } else {
      setCancelButtonPending(true);
    }
    await onUpdateOrder?.({
      conversationId,
      pendingToolCallId: message.pendingToolCallId ?? "",
      confirmationData: { ...structuredContent, items, confirmUpdate },
    });
  });

  const isCompleted = message.customerInputContent?.confirmed !== undefined;

  const itemsToAdd = useMemo(() => {
    return items.filter((item) => item.action === "add");
  }, [items]);

  const itemsToModify = useMemo(() => {
    return items.filter((item) => item.action === "remove");
  }, [items]);

  const isReadOnlyState = useMemo(() => {
    return (
      readOnly || cancelButtonPending || confirmButtonPending || isCompleted
    );
  }, [readOnly, cancelButtonPending, confirmButtonPending, isCompleted]);

  if (orderLoad.pending) {
    return (
      <Flex align="center" justify="center" py="md">
        <RedoSpinner size="md" />
      </Flex>
    );
  }

  const order = orderLoad.value;
  const orderLineItems =
    order?.shopify?.line_items.filter(
      (item: LineItem) => item.current_quantity && item.current_quantity > 0,
    ) || [];

  return (
    <Flex
      align="flex-end"
      as="form"
      className={classNames(
        css.subscribeToBackInStockBubbleContainer,
        css.messageSideLeft,
      )}
      dir="row"
      gap="md"
      px="lg"
      py="md"
    >
      <Flex dir="column" gap="xl">
        <Flex dir="column" gap="lg">
          <Flex dir="column" gap="sm">
            <Text fontSize="md" fontWeight="semibold" textColor="primary">
              Confirm order updates
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Review the changes to your order
            </Text>
          </Flex>

          {/* Items to Remove Section */}
          {itemsToModify.length > 0 && (
            <Flex dir="column" gap="md">
              <Text fontSize="sm" fontWeight="bold" textColor="secondary">
                Items to remove
              </Text>
              {itemsToModify.map((item, index) => (
                <Fragment key={item.variantId}>
                  <ItemRemovalRow
                    item={item}
                    onQuantityChange={handleQuantityChange}
                    orderLineItems={orderLineItems}
                    readOnly={isReadOnlyState}
                    storefrontAccessToken={storefrontAccessToken}
                    storeUrl={storeUrl}
                  />
                  {index < itemsToModify.length - 1 && <Divider />}
                </Fragment>
              ))}
            </Flex>
          )}

          {/* Items to Add Section */}
          {itemsToAdd.length > 0 && (
            <Flex dir="column" gap="md">
              <Text fontSize="sm" fontWeight="bold" textColor="secondary">
                Items to add
              </Text>
              {itemsToAdd.map((item, index) => (
                <Fragment key={index}>
                  <ItemAdditionRow
                    item={item}
                    onQuantityChange={handleQuantityChange}
                    onVariantChange={handleVariantChange}
                    readOnly={isReadOnlyState}
                    storefrontAccessToken={storefrontAccessToken}
                    storeUrl={storeUrl}
                  />
                  {index < itemsToAdd.length - 1 && <Divider />}
                </Fragment>
              ))}
            </Flex>
          )}

          {itemsToAdd.length > 0 && itemsToModify.length > 0 && <Divider />}

          {order?.shopify?.name && (
            <Flex align="center" dir="row" justify="space-between">
              <Text fontSize="sm" textColor="secondary">
                Order number
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {order.shopify.name}
              </Text>
            </Flex>
          )}
        </Flex>

        {isCompleted ? (
          <Flex alignSelf="center" dir="column" gap="sm">
            <Text fontSize="md" fontWeight="semibold" textColor="primary">
              {message.customerInputContent?.confirmed
                ? "Order updated successfully"
                : "Order update cancelled"}
            </Text>
          </Flex>
        ) : (
          <Flex align="flex-start" gap="sm">
            <RedoButton
              disabled={isReadOnlyState}
              hierarchy="secondary"
              onClick={() => {
                sinkPromise(confirmUpdate(false));
              }}
              pending={cancelButtonPending}
              text="Cancel"
            />
            <RedoButton
              buttonClassName={cancelOrderToolMessageCss.button}
              disabled={isReadOnlyState}
              hierarchy="primary"
              onClick={() => {
                sinkPromise(confirmUpdate(true));
              }}
              pending={confirmButtonPending}
              text="Confirm Updates"
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

export const UpdateOrderToolMessage = memo(function UpdateOrderToolMessage(
  props: UpdateOrderToolMessageProps,
) {
  const {
    message,
    onUpdateOrder,
    readOnly,
    getOrderDetails,
    conversationId,
    storeUrl,
    storefrontAccessToken,
  } = props;

  const itemCount = message.structuredContent?.items?.length || 0;

  const { title: confirmedTitle, subtitle: confirmedSubtitle } =
    getConfirmedStateContent(
      message,
      "Order updated successfully",
      "Order update cancelled",
      `${itemCount} item(s) updated in order`,
    );

  return (
    <ChatContentRenderer
      contentByState={{
        pending: (
          <UpdateOrderPendingState
            conversationId={conversationId}
            getOrderDetails={getOrderDetails}
            message={message}
            onUpdateOrder={onUpdateOrder}
            readOnly={readOnly}
            storefrontAccessToken={storefrontAccessToken}
            storeUrl={storeUrl}
          />
        ),
        confirmed: (
          <GenericConfirmedState
            subtitle={confirmedSubtitle}
            title={confirmedTitle}
          />
        ),
        rejected: (
          <GenericRejectedState
            subtitle="The order remains unchanged"
            title="Order update cancelled"
          />
        ),
      }}
      message={message}
    />
  );
});
